import { Grid, IconButton, Tooltip, Switch, Alert } from "@mui/material";
import {
  Button,
  CollapsibleSection,
  ConfirmDialog,
  MainCard,
  MuiTable,
  StatusTag,
  TextValidator,
  ValidatorForm,
} from "components";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import AdminAccountServices from "services/AdminAccountServices";
import EditIcon from "@mui/icons-material/Edit";
import { dateParse, scrollToTop } from "Utils";
import { useTranslation } from "react-i18next";

const IncidentTypeSetup = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: null,
    order: [["updatedAt", "DESC"]],
    type: "admin",
  });
  const [totalItems, setTotalItems] = useState(0);

  const getData = useCallback(async () => {
    setLoading(true);
    const response = await AdminAccountServices.get(filters);

    console.log("response", response);

    if (response.success) {
      setData(response?.data?.view?.data || []);
      setTotalItems(response?.data?.view?.totalItems || 0);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: name === "email" ? value.toLowerCase() : value,
    }));
  };

  const setPage = (page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: page,
    }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 1,
      limit: pageSize,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const apiData = Object.fromEntries(
        Object.entries(formData).filter(
          ([_, value]) => value !== null && value !== ""
        )
      );
      let response;
      if (isEdit) {
        response = await AdminAccountServices.update(editingData, apiData);
        if (response.success) {
          toast.success(response.message || t("adminAccounts.adminUpdated"));
        } else {
          toast.error(response.message || t("messages.error.updateFailed"));
        }
      } else {
        response = await AdminAccountServices.create({
          ...apiData,
          type: "admin",
        });
        if (response.success) {
          toast.success(response.message || t("adminAccounts.adminCreated"));
        } else {
          toast.error(response.message || t("messages.error.createFailed"));
        }
      }
      if (response.success) {
        setFormData({ code: "", description: "" });
        setIsEdit(false);
        setOpenEditModal(false);
        setEditingData(null);
        getData();
        scrollToTop();
      }
    } catch (error) {
      toast.error(t("messages.error.unexpectedError"));
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    const statusText =
      newStatus === "Active" ? t("common.active") : t("common.inactive");

    ConfirmDialog({
      type: newStatus === "Active" ? "success" : "warning",
      title: t("messages.confirm.statusChangeTitle", { status: statusText }),
      description: t("adminAccounts.statusChangeConfirm", {
        status: statusText,
      }),
      buttons: [
        {
          label: t("common.cancel"),
          className: "custom-confirm-dialog__btn--cancel",
        },
        {
          label: t("common.confirm"),
          className:
            newStatus === "Active"
              ? "custom-confirm-dialog__btn--success"
              : "custom-confirm-dialog__btn--warning",
          onClick: () => confirmStatusChange(id, newStatus),
        },
      ],
    });
  };

  const confirmStatusChange = async (id, newStatus) => {
    try {
      const response = await AdminAccountServices.updateStatus(id, {
        status: newStatus,
      });
      if (response.success) {
        toast.success(t("messages.success.statusChanged"));
        getData();
      } else {
        toast.error(response.message || t("messages.error.updateFailed"));
      }
    } catch {
      toast.error(t("messages.error.updateFailed"));
    }
  };

  return (
    <div>
      <MainCard>
        <CollapsibleSection
          title={t("adminAccounts.title")}
          collapsTrigger={openEditModal}
          buttonText={t("adminAccounts.addAdmin")}
        >
          <ValidatorForm onSubmit={handleSubmit} process={submitting}>
            <Grid container spacing={1}>
              <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                <TextValidator
                  label={t("common.name")}
                  name="name"
                  value={formData.name || ""}
                  onChange={handleInputChange}
                  validators={["required"]}
                  errorMessages={[t("common.error")]}
                  required={true}
                  disabled={loading}
                  placeholder={t("common.name")}
                />
              </Grid>
              <Grid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <TextValidator
                  label={t("auth.email")}
                  name="email"
                  value={formData.email || ""}
                  onChange={handleInputChange}
                  validators={["required", "isEmail"]}
                  errorMessages={[t("common.error"), t("auth.email")]}
                  required={true}
                  disabled={loading || isEdit}
                  placeholder={t("auth.email")}
                />
              </Grid>
              {/* <Grid size={{ xs: 12 }}>
                                <TextValidator
                                    label="Decription"
                                    name="description"
                                    value={formData.description || ''}
                                    onChange={handleInputChange}
                                    required={true}
                                    disabled={loading}
                                    multiline
                                    minRows={3}
                                    placeholder="Enter short description"
                                />
                            </Grid> */}
            </Grid>
            <Grid className="mt-2" container spacing={1}>
              <Grid>
                <Button type="submit" process={submitting}>
                  {isEdit ? t("buttons.edit") : t("buttons.save")}
                </Button>
              </Grid>
              <Grid>
                {openEditModal && (
                  <Button
                    className="btn-warning"
                    onClick={() => {
                      setFormData({ code: "", description: "" });
                      setIsEdit(false);
                      setOpenEditModal(false);
                      setEditingData(null);
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </ValidatorForm>
        </CollapsibleSection>
        <div>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Note:</strong> Only a maximum of four (4) admin accounts can
            be kept with active status at any time.
          </Alert>
          <MuiTable
            columns={[
              {
                accessorKey: "actions",
                header: t("common.actions"),
                Cell: ({ cell }) => {
                  const rowData = cell.row.original;
                  return (
                    <div>
                      <Tooltip title={t("common.edit")}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setIsEdit(false);
                            setOpenEditModal(false);
                            setTimeout(() => {
                              setFormData({
                                name: rowData.name,
                                email: rowData.email,
                                description: rowData.description,
                              });
                              setIsEdit(true);
                              setOpenEditModal(true);
                              setEditingData(rowData.id);
                              scrollToTop();
                            }, 500);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={t("common.status")}>
                        <Switch
                          size="small"
                          checked={rowData.status === "Active"}
                          onChange={async () => {
                            await changeStatus(rowData.id, rowData.status);
                          }}
                        />
                      </Tooltip>
                    </div>
                  );
                },
              },
              {
                accessorKey: "name",
                header: t("common.name"),
              },
              {
                accessorKey: "email",
                header: t("auth.email"),
              },

              {
                accessorKey: "status",
                header: t("common.status"),
                Cell: ({ cell }) => {
                  const status = cell.getValue();
                  return (
                    <StatusTag status={status.toLowerCase()} variant="small" />
                  );
                },
              },
              {
                accessorKey: "createdAt",
                header: t("common.createdAt"),
                Cell: ({ cell }) => {
                  return <span>{dateParse(cell.getValue())}</span>;
                },
              },
            ]}
            data={data}
            title="admin accounts"
            manualPagination={true}
            rowCount={totalItems}
            currentPage={filters.page}
            pageSize={filters.limit}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
          />
        </div>
      </MainCard>
    </div>
  );
};

export default IncidentTypeSetup;
